import React, { useContext, useEffect, useRef } from "react";
import { googleAds } from "appConfig";
import styles from "./styles/SearchPage.scss";
import { handleSearchPage } from "../../routes/Search";
import BreadcrumbBar from "../../components/BreadcrumbBar";
import { SearchSection } from "./SearchSection";
import SearchTags from "./SearchTags";
import { useSelector, useDispatch } from "react-redux";
import { selectForbidAds, setForbidAds } from "../../routes/Ad/ad.ducks";
import { selectCategories } from "../App/selectors";
import {
  checkEmptyArray,
  checkEmptyObject,
  isSearchMobile,
  scrollToTop,
  getUrlParams,
  getSearchAdCardId,
  takeLastClickedSearchAd
} from "../../services";
import SaveSearchForm from "./SaveSearchForm";
import getWidthContext from "../../HOCs/getWidthContext";
import HelmetComponent from "../../utils/HelmetHelper";
import { Context } from "../../GAContext";
import { CategoryNameHr, CurrentPage, LinkTargetEnum, QueryParam } from "../../models";
import {
  AdListFavoriteI,
  CategoriesI,
  RouteLinksI
} from "../../typings/routesTypes";
import {
  categoriesInterface,
  UserI
} from "../../typings/interfaces";
import GoogleAdInterstitial from "../../components/GoogleAdInterstitial";
import { CarStatistics } from "../ModulePage/CarStatistics/CarStatistics";

interface SearchPageProps {
  ads: AdListFavoriteI;
  categories: categoriesInterface[];
  controlFields: {};
  criteriaModules: {};
  favoriteIds: [];
  filteredCategories: CategoriesI[];
  getCreatorInfo: Function;
  getFilteredCategories: Function;
  history: History;
  isSaveSearchFormVisible: boolean;
  loading: boolean;
  location: Location;
  match: { path: string; url: string; isExact: boolean; params: {} };
  module: string;
  modules: any;
  numberOfControls: number;
  onResetAllTags: (...args: any[]) => any;
  onSaveClick: Function;
  onSaveSearchCancel: Function;
  onSaveSearchSubmit: Function;
  onSearchSubmit: Function;
  onTagRemove: (...args: any[]) => any;
  routeLinks: RouteLinksI[];
  saveSearch: Function;
  searchAds: Function;
  selectedCategory: string;
  setSearchText: Function;
  showHeaderLogoAndSearchBar: Function;
  staticContext: any;
  tags: [];
  updateCriteriaModules: Function;
  user: UserI | null;
  width: number;
  metaData: { title?: string; description?: string };
}

function SearchPage(props: SearchPageProps) {
  const {
    ads,
    isSaveSearchFormVisible,
    onSaveSearchCancel,
    onSaveSearchSubmit,
    routeLinks,
    module,
    width,
    showHeaderLogoAndSearchBar,
    metaData,
    filteredCategories,
    selectedCategory,
    tags,
  } = props;
  const [GaEvent, setGaEvent] = useContext(Context);
  const ref = useRef(null);
  const forbiddAds = useSelector(selectForbidAds);
  const dispatch = useDispatch();
  const allCategories = useSelector(selectCategories);
  // Ad the user opened from these exact results, if they just came back from it.
  // Read lazily: the stored value is consumed on read, so it must happen once.
  const pendingScrollAdIdRef = useRef(undefined);
  if (pendingScrollAdIdRef.current === undefined) {
    pendingScrollAdIdRef.current = takeLastClickedSearchAd();
  }

  useEffect(() => {
    showHeaderLogoAndSearchBar();
    // Returning to the ad we came from wins over the usual reset to the top.
    if (!pendingScrollAdIdRef.current) {
      scrollToTop();
    }
    setGaEvent(CurrentPage.search);

    return () => {
      setGaEvent("");
    };
  }, []);

  // Ids of the ads currently listed, so a re-fetch of the same results (e.g.
  // after toggling a favorite) isn't mistaken for a new search.
  const listedAdIdsRef = useRef("");

  useEffect(() => {
    if (!ads || !Array.isArray(ads.data)) return;
    const listedAdIds = ads.data.map((ad: any) => ad && ad.id).join(",");
    const isNewList = listedAdIds !== listedAdIdsRef.current;
    listedAdIdsRef.current = listedAdIds;

    const targetId = pendingScrollAdIdRef.current;
    if (!targetId) {
      // New results (e.g. a search from the header bar while this page is
      // already mounted) start from the top.
      if (isNewList) scrollToTop();
      return;
    }
    const inCurrentPage = ads.data.some((ad: any) => ad && ad.id === targetId);
    if (!inCurrentPage) {
      // Ad is no longer in these results (expired / filtered out) — drop the
      // request so subsequent renders don't keep hunting for it.
      pendingScrollAdIdRef.current = null;
      return;
    }
    const el = document.getElementById(getSearchAdCardId(targetId));
    if (el) {
      // Defer past paint so the newly rendered card has final geometry.
      window.setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth" });
      }, 0);
      pendingScrollAdIdRef.current = null;
    }
  }, [ads]);

  // Check if any ad in search results belongs to an 18+ category and block Google Ads
  useEffect(() => {
    if (ads && ads.data && ads.data.length > 0 && allCategories.length > 0) {
      const is18OldCategories = allCategories
        .filter(cat => cat.is18Old)
        .map(cat => cat.route);

      const hasAdFrom18PlusCategory = ads.data.some(
        (ad: any) => is18OldCategories.includes(ad.category)
      );

      if (hasAdFrom18PlusCategory) {
        dispatch(setForbidAds(true));
      } else {
        dispatch(setForbidAds(false));
      }
    }
  }, [ads, allCategories, dispatch]);

  const renderSearchSection = (isMobile: boolean) => {
    const {
      ads,
      onSaveClick,
      tags,
      onTagRemove,
      onResetAllTags,
      onSearchSubmit,
      categories,
      modules,
      module,
      location,
      numberOfControls,
      selectedCategory,
      loading,
      updateCriteriaModules,
      criteriaModules
    } = props;

    return (
      <SearchSection
        onSubmit={onSearchSubmit}
        categories={categories}
        modules={modules}
        module={module}
        ads={ads}
        // @ts-ignore
        tags={tags}
        onTagRemove={onTagRemove}
        onResetAllTags={onResetAllTags}
        location={location}
        hasSaveButton
        onSaveClick={onSaveClick}
        numberOfControls={numberOfControls}
        selectedCategory={selectedCategory}
        loading={loading}
        updateCriteriaModules={updateCriteriaModules}
        criteriaModules={criteriaModules}
        isMobile={isMobile}
        metaData={metaData}
        categoryName={categoryName}
        target={LinkTargetEnum.self}
        shouldHilightPromotedAds={true}
      >
        {tags.length > 0 ? (
          <SearchTags
            className={styles.tags}
            tags={tags}
            onTagRemove={onTagRemove}
            onResetAllTags={onResetAllTags}
          />
        ) : null}
      </SearchSection>
    );
  };

  const isMobile = isSearchMobile(width);

  const selectedCategoryObject = filteredCategories.filter(
    category => category.route == selectedCategory
  );

  const categoryName = checkEmptyArray(selectedCategoryObject)
    ? selectedCategoryObject[0].displayName
    : null;

  // Check if there's a text search parameter - only block indexing for text searches
  const queryParams = getUrlParams(props.location.search, QueryParam.searchQuery);
  const hasTextSearch = queryParams && queryParams.text;

  return (
    <React.Fragment>
      {checkEmptyObject(metaData) ? (
        <HelmetComponent
          title={checkEmptyObject(metaData) && metaData.title}
          desc={checkEmptyObject(metaData) && metaData.description}
          index={!hasTextSearch}
        />
      ) : (
        <HelmetComponent
          title={
            categoryName
              ? `${categoryName} | ${"INDEX OGLASI"}`
              : `${"Napredno pretraživanje"} | ${"INDEX OGLASI"}`
          }
          index={!hasTextSearch}
        />
      )}

      {isMobile && !forbiddAds && ads && ads.data && ads.data.length > 0 && (
        <GoogleAdInterstitial
          config={{
            ...googleAds.interstitialSlot,
            id: "googleAds.interstitialSlot"
          }}
        />
      )}

      {!module ? null : (
        <BreadcrumbBar
          className={styles.topBar}
          routeLinks={routeLinks}
          category={props.selectedCategory}
        />
      )}

      <div
        className={`${styles.limitedWidth} ${isMobile ? "" : styles.content
          } ads-seller`}
        ref={ref}
      >
        {renderSearchSection(isMobile)}
        <SaveSearchForm
          visible={isSaveSearchFormVisible}
          onCancel={onSaveSearchCancel}
          onCreate={onSaveSearchSubmit}
          category={props.selectedCategory}
          categories={props.categories}
          categoryName={categoryName}
          tags={tags}
        />
      </div>
      {window.location.pathname.includes(CategoryNameHr.car) && (
        <CarStatistics />
      )}

    </React.Fragment>
  );
}

SearchPage.defaultProps = {
  ads: {},
  selectedCategory: "",
  modules: []
};

export default handleSearchPage(getWidthContext(SearchPage), { runSearch: true });



// WEBPACK FOOTER //
// ./src/containers/SearchPage/index.tsx